/**
 * StorageAdapter facade.
 *
 * The frontend imports persistence and sync-URL helpers from this module —
 * NOT directly from y-indexeddb / window.localStorage / window.location.
 * The active adapter is selected once at module load based on a runtime
 * probe.
 *
 * Phase 1 ships only the web adapter. Phase 2 adds a tauri-adapter and
 * the runtime probe selects it when running inside Tauri's webview.
 *
 * See docs/architecture/storage-adapter.md for the full contract.
 */

import * as web from "./web-adapter.js";
import * as tauri from "./tauri-adapter.js";

const isTauri =
  typeof window !== "undefined" &&
  typeof window.__TAURI_INTERNALS__ !== "undefined";

// The Tauri adapter is statically importable in the web build because
// it never touches Tauri APIs at module load — `window.__TAURI_INTERNALS__`
// is only read inside function bodies. Bundlers tree-shake the unused
// branch in each runtime.
const adapter = isTauri ? tauri : web;
if (typeof console !== "undefined") {
  console.log(`[StorageAdapter] Active runtime: ${isTauri ? "tauri" : "web"}`);
}

// CRDT cache
export const createDocPersistence  = adapter.createDocPersistence;
export const createUserPersistence = adapter.createUserPersistence;
export const deleteDocCache        = adapter.deleteDocCache;
export const clearAllCaches        = adapter.clearAllCaches;
export const clearAccountCaches    = adapter.clearAccountCaches;

// Sync transport endpoints
export const getSyncUrl       = adapter.getSyncUrl;
export const isSyncAvailable  = adapter.isSyncAvailable;

// Key-value preferences
export const getPref            = adapter.getPref;
export const setPref            = adapter.setPref;
export const removePref         = adapter.removePref;
export const clearPrefsByPrefix = adapter.clearPrefsByPrefix;

// Tauri-only surface (undefined on web — feature-detect before calling).
export const getVaultPath = adapter.getVaultPath;
export const setVaultPath = adapter.setVaultPath;
// Phase 5a: opt-in online mode.
export const loadAdapterConfig = adapter.loadAdapterConfig;
export const getServerOrigin = adapter.getServerOrigin;
export const setServerUrl = adapter.setServerUrl;
// Phase 5b: global fetch routing shim (Tauri online mode only).
export const installFetchProxy = adapter.installFetchProxy;

export const readDocSize = adapter.readDocSize;

// A map's file in the desktop app's vault folder (moving an online map to
// this device). The browser has no such file.
const desktopOnly = async () => { throw new Error("Only the desktop app keeps maps in a folder"); };
export const mergeIntoMapFile = adapter.mergeIntoMapFile || desktopOnly;
export const readMapFile = adapter.readMapFile || desktopOnly;
// Its files, raw: "main" (<vault>/<id>.orbital) or "cache" (.orbital-cache/);
// no bytes when there is no file.
export const readVaultFile = adapter.readVaultFile || desktopOnly;
export const writeVaultFile = adapter.writeVaultFile || desktopOnly;

// Encrypted attachments kept on this device, by scope: "device" (maps stored
// on this device), "offline" (copies of online maps' files) and "waiting"
// (files attached to an online map, not sent yet).
export const putStoredAttachment = adapter.putStoredAttachment;
export const getStoredAttachment = adapter.getStoredAttachment;
export const deleteStoredAttachment = adapter.deleteStoredAttachment;
export const listStoredAttachments = adapter.listStoredAttachments;
export const deleteStoredMapAttachments = adapter.deleteStoredMapAttachments;
export const listStoredAttachmentMaps = adapter.listStoredAttachmentMaps;

// Await local saves before logout, navigation or a configuration switch.
export const flushPendingWrites = adapter.flushPendingWrites || (async () => {});
export const reloadApp = adapter.reloadApp || (async (beforeReload) => {
  await beforeReload?.();
  window.location.reload();
  return true;
});
